import { plainDateFromEpochDay } from '../../../core/services/plain-date';

import type { GanttItem, GanttPatch, Row, Span } from '../types';

export type DragMode = 'move' | 'resize-start' | 'resize-end';

/**
 * The one place every drag rule lives, so the pointer hook and (eventually) the keyboard path
 * can never diverge: minimum span of one day, never inverted, `move` translates both ends
 * together.
 */
export const previewSpanFor = (span: Span, mode: DragMode, dayDelta: number): Span => {
  if (mode === 'move') {
    return { startDay: span.startDay + dayDelta, endDay: span.endDay + dayDelta };
  }

  if (mode === 'resize-start') {
    return { startDay: Math.min(span.startDay + dayDelta, span.endDay), endDay: span.endDay };
  }

  return { startDay: span.startDay, endDay: Math.max(span.endDay + dayDelta, span.startDay) };
};

/**
 * Only the edges that actually changed become a patch field, so dragging one end of a single-date
 * item sets only the date that was absent rather than rewriting a date the user never touched.
 */
export const patchesFor = (item: GanttItem, span: Span, preview: Span): GanttPatch[] => {
  const patch: GanttPatch = { id: item.id };

  if (preview.startDay !== span.startDay) {
    patch.startDate = plainDateFromEpochDay(preview.startDay);
  }
  if (preview.endDay !== span.endDay) {
    patch.dueDate = plainDateFromEpochDay(preview.endDay);
  }

  return Object.keys(patch).length > 1 ? [patch] : [];
};

/**
 * Spec §8: who a drag gesture can ever touch. A group row is never draggable — its span is a
 * rollup of its children's, and dragging it would silently rewrite every descendant's dates at
 * once, which §8 does not ask for ("Group rows: not draggable, matching today"). An undated row
 * has no span for `useBarDrag` to drag FROM. `readOnly` (`GanttConfig`, chart-wide) and
 * `item.editable === false` (`GanttItem`, one item) are the two consumer-facing escape hatches.
 *
 * One function, fed into `useBarDrag`'s `disabled` by BOTH `Bar` and `GroupBar`, rather than each
 * writing its own conditional — so the eligibility rule cannot quietly drift between the two.
 */
export const isDragDisabledFor = (row: Row, readOnly?: boolean): boolean =>
  row.isGroup || !row.span || !!readOnly || row.item.editable === false;

/**
 * Spec's `+` affordance (Task 20): who may turn an UNDATED row into a dated one. This is not
 * `!isDragDisabledFor` — that function returns `true` for `!row.span` unconditionally (its whole
 * point is "there is nothing to drag FROM"), which would make an undated row read as disabled for
 * every purpose, including the one thing it CAN do. Adding needs the opposite polarity on that one
 * field (only an UNDATED row is eligible to add) while keeping the other three gates identical:
 * `row.isGroup` (a group's span is a rollup of its children's; setting dates on the group directly
 * has no defined meaning and would not touch a single real item), `readOnly` (chart-wide), and
 * `item.editable === false` (one item's own escape hatch). One function, read by `EmptyRowAdd`
 * alone today, so a future second entry point for "add dates" cannot silently diverge on the rule.
 */
export const isAddDisabledFor = (row: Row, readOnly?: boolean): boolean =>
  row.isGroup || !!row.span || !!readOnly || row.item.editable === false;

/** The 4px hit margin, matching the `::before`/`::after` strips in `bar.module.scss`. */
export const RESIZE_HIT_PX = 4;

/**
 * Which of the three gestures a `pointerdown` starts. Mirrors the pre-rebuild
 * `draggable-strip.tsx` mousedown handler's edge hit-test: its `::before`/`::after` 4px strips
 * are CSS-only pseudo-elements, so there is nothing for React to attach a listener to directly —
 * this reproduces the same "within 4px of an edge" rule against the bar's own measured rect.
 * Pure and DOM-agnostic (`rect` is a plain `{left, right}`, not a real `DOMRect`) so it can be
 * tested without a real layout engine.
 *
 * NOT symmetric with the visible strip: `bar.module.scss`'s `::before`/`::after` are 0.4rem (4px)
 * wide and CENTRED on the edge (2px in, 2px out), so 2-4px inside the edge shows a `grab` cursor
 * but resolves to a resize here. This is not a new bug — it is a faithful port of
 * `draggable-strip.tsx`'s original one-sided `leftEdgeDelta < 4 && leftEdgeDelta > -4` test, which
 * had the identical mismatch. Do not "fix" the asymmetry without confirming the intended affordance
 * width first; it is parity, not an oversight.
 */
export const modeForHit = (clientX: number, rect: { left: number; right: number }): DragMode => {
  if (clientX - rect.left <= RESIZE_HIT_PX) return 'resize-start';
  if (rect.right - clientX <= RESIZE_HIT_PX) return 'resize-end';

  return 'move';
};
